package br.com.vvrefeicoes.portal.feature.forms

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppBadge
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.AppToast
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.layout.appLayoutMetrics
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.domain.model.allowsAny
import com.composables.icons.lucide.ArrowRight
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.ChevronUp
import com.composables.icons.lucide.Copy
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Plus
import com.composables.icons.lucide.Search
import kotlinx.coroutines.delay
import org.jetbrains.compose.resources.StringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.forms_download_pending

private sealed interface PendingFormAction {
    data class Copy(
        val id: String,
    ) : PendingFormAction

    data class Toggle(
        val id: String,
        val active: Boolean,
    ) : PendingFormAction
}

@Composable
fun FormsListScreen(
    onCreate: () -> Unit,
    onEdit: (String) -> Unit,
    onView: (String) -> Unit,
) {
    val appGraph = LocalAppGraph.current
    val layout = appLayoutMetrics()
    val permissions = rememberSessionPermissions()
    val scope = rememberCoroutineScope()
    val visibleSectors = remember(permissions) { allowedFormSectors(permissions).map { it.label }.toSet() }
    val controller =
        remember(appGraph.formsRepository, scope, visibleSectors) {
            FormsListController(appGraph.formsRepository, scope, visibleSectors)
        }
    LaunchedEffect(controller) { controller.init() }
    val canCreate =
        visibleSectors.isNotEmpty() &&
            permissions.allows(PermissionKeys.FORMULARIOS, PermissionAction.CREATE)
    val canEdit =
        visibleSectors.isNotEmpty() &&
            permissions.allows(PermissionKeys.FORMULARIOS, PermissionAction.EDIT)
    val canApprove = permissions.allowsAny(PermissionKeys.FORMULARIOS_APPROVE)
    var toast by remember { mutableStateOf<StringResource?>(null) }
    var pendingAction by remember { mutableStateOf<PendingFormAction?>(null) }

    LaunchedEffect(toast) {
        if (toast != null) {
            delay(2500)
            toast = null
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        AppScrollColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = layout.pagePadding,
            verticalArrangement = Arrangement.spacedBy(layout.pageGap),
            footer = { AppFooter() },
        ) {
            Text(
                text = "Formulários",
                style = MaterialTheme.typography.headlineMedium,
            )

            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
            ) {
                AppTextField(
                    value = controller.query,
                    onValueChange = controller::updateQuery,
                    placeholder = "Pesquise por título ou tipo do formulário",
                    leadingIcon = { Icon(Lucide.Search, contentDescription = null) },
                    modifier = Modifier.weight(1f),
                )
                if (canCreate) {
                    AppButton(
                        text = "Criar formulário",
                        onClick = onCreate,
                        leadingIcon = Lucide.Plus,
                    )
                }
            }

            controller.errorMessage?.let { message ->
                AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
            }

            val groups =
                FormStatus.visibleOrder.mapNotNull { status ->
                    val forms = controller.items.filter { it.status == status }
                    if (forms.isEmpty()) null else status to forms
                }
            if (controller.loading && controller.items.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space48), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator()
                }
            } else if (groups.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space48)) {
                    EmptyState(message = "Nenhum formulário encontrado.")
                }
            } else {
                groups.forEach { (status, forms) ->
                    FormStatusSection(
                        status = status,
                        forms = forms,
                        expanded = controller.isExpanded(status),
                        canEdit = canEdit,
                        canCreate = canCreate,
                        canApprove = canApprove,
                        permissions = permissions,
                        onToggle = { controller.toggleGroup(status) },
                        onEdit = onEdit,
                        onView = onView,
                        onCopyRequest = { id -> pendingAction = PendingFormAction.Copy(id) },
                        onDownload = { toast = Res.string.forms_download_pending },
                        onSetActiveRequest = { id, active ->
                            pendingAction = PendingFormAction.Toggle(id, active)
                        },
                    )
                }
            }
        }
        toast?.let { message ->
            AppToast(
                message = message,
                modifier = Modifier.align(Alignment.BottomCenter).padding(bottom = AppSpacing.space24),
            )
        }
        pendingAction?.let { action ->
            val title =
                when (action) {
                    is PendingFormAction.Copy -> "Copiar formulário"
                    is PendingFormAction.Toggle ->
                        if (action.active) "Ativar formulário" else "Inativar formulário"
                }
            val message =
                when (action) {
                    is PendingFormAction.Copy ->
                        "Será criado um novo rascunho a partir deste formulário."
                    is PendingFormAction.Toggle ->
                        if (action.active) {
                            "Deseja ativar este formulário?"
                        } else {
                            "Deseja inativar este formulário?"
                        }
                }
            AppModalCard(
                title = title,
                onDismiss = { pendingAction = null },
            ) {
                Text(text = message)
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8, Alignment.End),
                ) {
                    AppButton(
                        text = "Cancelar",
                        onClick = { pendingAction = null },
                        variant = PortalButtonVariant.Text,
                    )
                    AppButton(
                        text = if (action is PendingFormAction.Copy) "Copiar" else "Confirmar",
                        onClick = {
                            when (action) {
                                is PendingFormAction.Copy -> {
                                    pendingAction = null
                                    controller.copy(action.id, onEdit)
                                }
                                is PendingFormAction.Toggle -> {
                                    pendingAction = null
                                    controller.setActive(action.id, action.active)
                                }
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun FormStatusSection(
    status: FormStatus,
    forms: List<FormSummary>,
    expanded: Boolean,
    canEdit: Boolean,
    canCreate: Boolean,
    canApprove: Boolean,
    permissions: Map<String, Set<PermissionAction>>,
    onToggle: () -> Unit,
    onEdit: (String) -> Unit,
    onView: (String) -> Unit,
    onCopyRequest: (String) -> Unit,
    onDownload: () -> Unit,
    onSetActiveRequest: (String, Boolean) -> Unit,
) {
    val layout = appLayoutMetrics()
    Column(verticalArrangement = Arrangement.spacedBy(layout.cardGap)) {
        Row(
            modifier = Modifier.fillMaxWidth().clickable(onClick = onToggle).padding(vertical = AppSpacing.space4),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Icon(
                imageVector = if (expanded) Lucide.ChevronUp else Lucide.ChevronDown,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(20.dp),
            )
            Text(
                text = status.label,
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.SemiBold,
                color = MaterialTheme.colorScheme.onSurface,
            )
        }
        if (expanded) {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val columns =
                    when {
                        maxWidth < 680.dp -> 1
                        maxWidth < 1080.dp -> 2
                        else -> 3
                    }
                Column(verticalArrangement = Arrangement.spacedBy(layout.cardGap)) {
                    forms.chunked(columns).forEach { rowForms ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(layout.cardGap),
                        ) {
                            rowForms.forEach { form ->
                                FormCard(
                                    form = form,
                                    canEdit = canEdit && form.sectors.hasAllFormSectors(permissions),
                                    canCreate = canCreate && form.sectors.hasAllFormSectors(permissions),
                                    canApprove = canApprove && form.sectors.hasAllFormSectors(permissions),
                                    onEdit = { onEdit(form.id) },
                                    onView = { onView(form.id) },
                                    onCopyRequest = { onCopyRequest(form.id) },
                                    onDownload = onDownload,
                                    onSetActiveRequest = { active -> onSetActiveRequest(form.id, active) },
                                    modifier = Modifier.weight(1f),
                                )
                            }
                            repeat(columns - rowForms.size) {
                                Spacer(modifier = Modifier.weight(1f))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SectorBadge(sector: FormSector) {
    val spec = formSectorStyle(sector)
    Surface(
        shape = AppShapes.medium,
        color = spec.containerColor,
        modifier = Modifier.size(40.dp),
    ) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
            Icon(
                imageVector = spec.icon,
                contentDescription = sector.label,
                tint = spec.contentColor,
                modifier = Modifier.size(20.dp),
            )
        }
    }
}

@Composable
private fun FormCard(
    form: FormSummary,
    canEdit: Boolean,
    canCreate: Boolean,
    canApprove: Boolean,
    onEdit: () -> Unit,
    onView: () -> Unit,
    onCopyRequest: () -> Unit,
    onDownload: () -> Unit,
    onSetActiveRequest: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
) {
    val layout = appLayoutMetrics()
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(layout.cardPadding),
            verticalArrangement = Arrangement.spacedBy(layout.cardGap),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
                verticalAlignment = Alignment.Top,
            ) {
                Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                    Text(
                        text = form.title,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.SemiBold,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                    )
                    Text(
                        text = form.sectors.joinToString(", ").ifBlank { "Sem setor" } + " · v${form.version}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
                Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                    form.sectors
                        .mapNotNull(FormSector::fromLabel)
                        .distinct()
                        .forEach { sector ->
                            SectorBadge(sector)
                        }
                }
            }
            Text(
                text = form.description,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 3,
                overflow = TextOverflow.Ellipsis,
            )
            Text(
                text = "Criado por ${form.createdBy}",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            form.approvedBy?.let { approvedBy ->
                Text(
                    text = "Aprovado por $approvedBy",
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            Text(
                text = formUpdatedAtLabel(form.updatedAt),
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            if (form.obsolete) {
                AppBadge(
                    text = "Obsoleto",
                    containerColor = AppPalette.Neutral200,
                    contentColor = AppPalette.Neutral900,
                )
            }
            Spacer(modifier = Modifier.size(AppSpacing.space4))
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
            ) {
                if (canApprove && !form.obsolete) {
                    when (form.status) {
                        FormStatus.APPROVED ->
                            Switch(
                                checked = true,
                                onCheckedChange = { onSetActiveRequest(it) },
                            )
                        FormStatus.INACTIVE ->
                            Switch(
                                checked = false,
                                onCheckedChange = { onSetActiveRequest(it) },
                            )
                        else -> Unit
                    }
                }
                if (canCreate && (form.status == FormStatus.APPROVED || form.status == FormStatus.INACTIVE)) {
                    IconButton(onClick = onCopyRequest) {
                        Icon(
                            imageVector = Lucide.Copy,
                            contentDescription = "Copiar formulário",
                            tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
                if (form.status == FormStatus.APPROVED || form.status == FormStatus.INACTIVE) {
                    IconButton(onClick = onDownload) {
                        Icon(
                            imageVector = Lucide.Download,
                            contentDescription = "Baixar formulário",
                            tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        )
                    }
                }
                Spacer(modifier = Modifier.weight(1f))
                Row(
                    modifier =
                        Modifier
                            .padding(horizontal = AppSpacing.space8)
                            .heightIn(min = 48.dp)
                            .clickable {
                                if (form.status == FormStatus.DRAFT && canEdit) onEdit() else onView()
                            },
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
                ) {
                    Text(
                        text = if (form.status == FormStatus.DRAFT) "Ir para edição" else "Ver formulário",
                        style = MaterialTheme.typography.labelLarge,
                        color = MaterialTheme.colorScheme.primary,
                    )
                    Icon(
                        imageVector = Lucide.ArrowRight,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
        }
    }
}
